import React from 'react';
import { View, Text } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { getFocusedRouteNameFromRoute } from '@react-navigation/native';
import { StatusBar } from 'expo-status-bar';
import { useTranslation } from 'react-i18next';
import useTheme from '../hooks/useTheme';
import { MCPTestIds } from '../utils/mcp-test-helpers';
import { TabParamList } from './types/navigation.types';
import { SCREEN_NAMES } from './config/constants';
import { AppleTabBar } from './components/AppleTabBar';
import { 
  DashboardStack, 
  PurchaseStack, 
  VendorStack, 
  InventoryStack,
  EnterpriseStack
} from './stacks';

const Tab = createBottomTabNavigator<TabParamList>();

// Routes where tab bar should be hidden for full-screen experiences
const TAB_HIDDEN_ROUTES = [
  // Purchase flow - full screen capture and extraction
  'CapturePurchase',
  'PurchaseExtraction',
  'PurchaseDetail',
  'PurchaseEdit',
  'VendorPayment',
  'PurchaseOrderDetail',
  'DeliveryCheck',
  
  // Other full-screen experiences
  'ExportInvoices',
  'NotificationCenter',
  'AIInsights',
  
  // Inventory management screens that need full space
  'InventoryItemDetail',
  'StockMovements',
  
  // Vendor detail screens
  'SupplierDetail',
  'SupplierEdit',
  
  // Enterprise full-screen experiences
  'RequisitionForm',
  'RequisitionDetail',
  'BudgetDashboard',
  'QualityInspection',
  'DamageAssessment',
  'RemediationDashboard',
  'InventoryAnalyticsDashboard',
];

// Helper function to determine tab bar visibility
const getTabBarVisibility = (route: any) => {
  const routeName = getFocusedRouteNameFromRoute(route);
  // If no focused route, check the route state for the current route
  const activeRouteName = routeName ?? route.state?.routes[route.state.index]?.name ?? '';
  
  return TAB_HIDDEN_ROUTES.includes(activeRouteName) ? 'none' : 'flex';
};

export function TabNavigator() {
  const { t } = useTranslation();
  const { colors, isDark } = useTheme();



  return (
    <>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <Tab.Navigator
        tabBar={(props) => <AppleTabBar {...props} />}
        screenOptions={{
          headerShown: false,
          sceneContainerStyle: { flex: 1 }, // Ensure scenes take full height on web
        }}
      >
        <Tab.Screen
          name={SCREEN_NAMES.DASHBOARD}
          component={DashboardStack}
          options={({ route }) => ({
            title: t('dashboard.tabTitle'),
            tabBarTestID: MCPTestIds.TAB_HOME,
            tabBarAccessibilityLabel: 'Dashboard tab',
            tabBarStyle: {
              display: getTabBarVisibility(route),
            },
          })}
        />
        <Tab.Screen
          name={SCREEN_NAMES.PURCHASES}
          component={PurchaseStack}
          options={({ route }) => ({
            title: t('purchase.tabTitle', 'Purchases'),
            tabBarTestID: MCPTestIds.TAB_PURCHASES,
            tabBarAccessibilityLabel: 'Purchases tab',
            tabBarStyle: {
              display: getTabBarVisibility(route),
            },
          })}
        />
        <Tab.Screen
          name={SCREEN_NAMES.VENDORS}
          component={VendorStack}
          options={({ route }) => ({
            title: t('vendor.tabTitle', 'Vendors'),
            tabBarTestID: MCPTestIds.TAB_VENDORS,
            tabBarAccessibilityLabel: 'Vendors tab',
            tabBarStyle: {
              display: getTabBarVisibility(route),
            },
          })}
        />
        <Tab.Screen
          name={SCREEN_NAMES.INVENTORY}
          component={InventoryStack}
          options={({ route }) => ({
            title: t('inventory.tabTitle', 'Inventory'),
            tabBarTestID: MCPTestIds.TAB_INVENTORY,
            tabBarAccessibilityLabel: 'Inventory tab',
            tabBarStyle: {
              display: getTabBarVisibility(route),
            },
          })}
        />
        <Tab.Screen
          name="Enterprise"
          component={EnterpriseStack}
          options={({ route }) => ({
            title: t('enterprise.tabTitle', 'Enterprise'),
            tabBarTestID: 'TAB_ENTERPRISE',
            tabBarAccessibilityLabel: 'Enterprise tab',
            tabBarStyle: {
              display: getTabBarVisibility(route),
            },
          })}
        />
      </Tab.Navigator>
    </>
  );
}